import { formatInternational } from 'lib-shared-phone-numbers';
import { Channel, Media } from 'lib-shared-types-channels';

import { mediaToChannels } from 'utils/channels/channels';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import {
  ConversationTitleArgs,
  ConversationTitlePrefix,
  ConversationTitleRequester,
} from './conversationTitle.types';

const titlePrefixByMedia: Record<Media, ConversationTitlePrefix> = {
  interactivechat: 'Chat with',
  messaging: 'Messaging with',
  email: {
    inbound: 'Inbound email from',
    outbound: 'Outbound email to',
    default: 'Email with',
  },
  speak: {
    inbound: 'Inbound call from',
    outbound: 'Outbound call to',
    internal: 'Internal call with',
    default: 'Call with',
  },
};

/** Channels whose title differs from the one of their media. */
const titlePrefixByChannel: Partial<Record<Channel, ConversationTitlePrefix>> =
  {
    contact_form: 'Contact form submission by',
    callback: 'Callback request from',
    voicemail: 'Voicemail from',
    trustpilotreviews: 'Trustpilot review by',
    yotporeviews: 'Yotpo review by',
  };

const titlePrefixFallback = 'Conversation with';

const mediaByChannel = new Map<Channel, Media>(
  (Object.entries(mediaToChannels) as [Media, readonly Channel[]][]).flatMap(
    ([media, channels]) => channels.map((channel) => [channel, media]),
  ),
);

/**
 * Gets the requester's display name for the title. Voice channels fall back to
 * the internationally formatted phone number and never to the email.
 */
function getRequesterName(
  isVoice: boolean,
  requester: ConversationTitleRequester | null | undefined,
): string {
  if (!requester) return 'Anonymous';

  if (isVoice) {
    return getUserDisplayName(
      {
        ...requester,
        email: null,
        phone_number: requester.phone_number
          ? (formatInternational(requester.phone_number) ??
            requester.phone_number)
          : null,
      },
      'Anonymous',
    );
  }

  return getUserDisplayName(requester, 'Anonymous');
}

/**
 * Gets the conversation title, which is the subject when present, otherwise a
 * description based on the channel, direction and requester.
 *
 * @example
 *   getConversationTitle({ channel: 'email', subject: 'Refund request' });
 *   // 'Refund request'
 *
 *   getConversationTitle({ channel: 'email', direction: 'outbound', requester });
 *   // 'Outbound email to Jane Doe'
 *
 *   getConversationTitle({ channel: 'widget_chat', requester });
 *   // 'Chat with Jane Doe'
 *
 *   getConversationTitle({ channel: 'pstn_phone', direction: 'inbound', requester });
 *   // 'Inbound call from +45 81 82 79 14'
 *
 *   getConversationTitle({ channel: 'trustpilotreviews' });
 *   // 'Trustpilot review by Anonymous'
 */
export function getConversationTitle({
  channel,
  direction,
  subject,
  requester,
}: ConversationTitleArgs): string {
  const trimmedSubject = subject?.trim();
  if (trimmedSubject) return trimmedSubject;

  const media = mediaByChannel.get(channel);
  const title =
    titlePrefixByChannel[channel] ??
    (media ? titlePrefixByMedia[media] : titlePrefixFallback);

  const prefix =
    typeof title === 'string'
      ? title
      : ((direction && title[direction]) ?? title.default);

  return `${prefix} ${getRequesterName(media === 'speak', requester)}`;
}
